// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import { message } from "antd";
import { Homeserver } from "./api";
import { homeserverBase, isNativeApp } from "./server";

// UnifiedPush (ntfy) background push. The app registers with a UnifiedPush
// distributor (the ntfy app); the distributor issues an endpoint, which we report
// to the homeserver. The homeserver POSTs a CONTENT-FREE wake-up there when the
// user is offline; the distributor delivers it to OUR app's native service
// (capacitor-unifiedpush), which shows a Zalmoxis notification — even when killed.
// No Firebase. Native-only; the web is a no-op.

// pushStatus is the LAST registration outcome — push otherwise fails silently, which is
// impossible to debug on a device. Logged (grep `[push]` in `adb logcat`), surfaced as a toast
// on failure, and readable by the UI via getPushStatus().
export type PushStatus = {
  state: "idle" | "ok" | "disabled" | "no-distributor" | "rejected" | "error";
  detail?: string;
  endpoint?: string;
};
let pushStatus: PushStatus = { state: "idle" };
export function getPushStatus(): PushStatus {
  return pushStatus;
}
function fail(s: PushStatus, toast: string) {
  pushStatus = s;
  console.error("[push]", s.state, "-", s.detail ?? "");
  void message.warning(`Push: ${toast}`, 6);
}

// Whether the user wants notifications (default ON). Persisted so launch + the settings toggle
// agree; the native side keeps its own flag too (the boot receiver can't read localStorage).
const ENABLED_KEY = "zalmoxis.push.enabled";
export function isPushEnabled(): boolean {
  return localStorage.getItem(ENABLED_KEY) !== "false";
}
export function setPushEnabled(on: boolean): void {
  localStorage.setItem(ENABLED_KEY, on ? "true" : "false");
}

// batteryExempt reports whether the app is exempt from battery optimization (true on web). The
// key reliability lever on Samsung/aggressive OEMs.
export async function batteryExempt(): Promise<boolean> {
  if (!isNativeApp()) {
    return true;
  }
  try {
    const { UnifiedPush } = await import("capacitor-unifiedpush");
    return (await UnifiedPush.getBatteryStatus()).exempt;
  } catch {
    return true;
  }
}

// requestBatteryExempt shows the system battery-optimization dialog (no-op if already exempt).
export async function requestBatteryExempt(): Promise<void> {
  if (!isNativeApp()) {
    return;
  }
  try {
    const { UnifiedPush } = await import("capacitor-unifiedpush");
    await UnifiedPush.requestBatteryExemption();
  } catch {
    /* ignore */
  }
}

// disableNtfyPush turns notifications OFF: stop the foreground service (and stop it restarting on
// boot) and remove this device's pusher so the homeserver no longer sends wake-ups.
export async function disableNtfyPush(token: string): Promise<void> {
  setPushEnabled(false);
  if (isNativeApp()) {
    try {
      const { UnifiedPush } = await import("capacitor-unifiedpush");
      await UnifiedPush.stopEmbedded();
    } catch (e) {
      console.warn("[push] stopEmbedded failed", e);
    }
  }
  try {
    await new Homeserver().deletePusher(token);
  } catch {
    /* best effort */
  }
  pushStatus = { state: "idle" };
}

// registerNtfyPush registers with the distributor and reports the push endpoint to the
// homeserver. Idempotent across logins; loud at every failure gate.
export async function registerNtfyPush(token: string, userId: string): Promise<void> {
  if (!isNativeApp()) {
    return;
  }
  try {
    const hs = new Homeserver();
    const cfg = await hs.pushConfig(token);
    if (!cfg.enabled) {
      fail({ state: "disabled", detail: "homeserver reports push disabled (no push.ntfy_servers configured?)" }, "the homeserver has push disabled");
      return;
    }
    const server = cfg.ntfy_server ?? "";
    if (!server) {
      fail({ state: "disabled", detail: "homeserver push is enabled but advertised no ntfy_server" }, "the homeserver provided no ntfy server");
      return;
    }
    console.info("[push] starting EMBEDDED distributor; ntfy server =", server);
    const { UnifiedPush } = await import("capacitor-unifiedpush");
    // EMBEDDED: the app is its own distributor — start the foreground service that subscribes to
    // a minted topic on the homeserver's ntfy, and report that endpoint. No separate ntfy app.
    let endpoint = "";
    try {
      endpoint = (await UnifiedPush.startEmbedded({ server })).endpoint;
    } catch (e) {
      fail({ state: "error", detail: "startEmbedded failed: " + (e instanceof Error ? e.message : String(e)) }, "could not start the push service");
      return;
    }
    if (!endpoint) {
      fail({ state: "error", detail: "startEmbedded returned no endpoint" }, "could not start the push service");
      return;
    }
    try {
      await hs.registerPusher(token, "ntfy", endpoint); // plaintext (no Web Push keys) to your own ntfy
      pushStatus = { state: "ok", endpoint };
      console.info("[push] embedded pusher registered:", endpoint);
      // Hand the session token to the native side so the Decline button can relay a hangup.
      void UnifiedPush.setIdentity({ userId, homeserver: homeserverBase() }).catch(() => undefined);
      // Ask to be exempt from battery optimization (no-op if already) — without this Samsung
      // Freecess / Doze freezes the push service. Shows the system dialog once.
      void UnifiedPush.requestBatteryExemption().catch(() => undefined);
    } catch (e) {
      // If this rejects, the homeserver's push.ntfy_servers allowlist doesn't include `server`.
      fail(
        { state: "rejected", detail: (e instanceof Error ? e.message : String(e)) + " — endpoint: " + endpoint, endpoint },
        "the homeserver rejected this device's endpoint (not on push.ntfy_servers?)",
      );
    }
  } catch (e) {
    fail({ state: "error", detail: e instanceof Error ? e.message : String(e) }, "registration error — see logs");
  }
}

// launchRoomId returns the room a tapped push notification asked to open ("" if not
// launched from a notification). Native-only.
export async function launchRoomId(): Promise<{ roomId: string; action: string }> {
  if (!isNativeApp()) {
    return { roomId: "", action: "" };
  }
  try {
    const { UnifiedPush } = await import("capacitor-unifiedpush");
    const { roomId, action } = await UnifiedPush.getLaunchRoomId();
    return { roomId, action: action ?? "" };
  } catch {
    return { roomId: "", action: "" };
  }
}

// stopCallRinging clears the native incoming-call ringtone + notification — call it when the
// call is being handled in-app (answered / declined / connected / ended).
export async function stopCallRinging(): Promise<void> {
  if (!isNativeApp()) {
    return;
  }
  try {
    const { UnifiedPush } = await import("capacitor-unifiedpush");
    await UnifiedPush.stopRinging();
  } catch {
    /* ignore */
  }
}
